import React, { useEffect, useState } from 'react';
import { usePreferencesStore } from '../../store/preferencesStore';
import { fetchLinkPreview, getCachedLinkPreview } from '../../utils/linkPreview';
import { isImageUrl, openExternalUrl, videoEmbedSrc } from './chatLinks';

/**
 * Opt-in embed for the first link in a message. Nothing renders unless the
 * `linkPreviews` preference is on. A direct image/gif URL is shown inline as the
 * image itself (no fetch); any other URL is unfurled into an OG/meta card.
 */
export function LinkPreview({ url }) {
  const enabled = usePreferencesStore((s) => s.linkPreviews);
  const embedSrc = enabled ? videoEmbedSrc(url) : null;
  const directImage = enabled && !embedSrc && isImageUrl(url);
  // Card path only: direct images and video embeds need no fetch/parse.
  const cardPath = Boolean(enabled && url && !embedSrc && !directImage);
  // Initial state comes from the synchronous cache: on remount (which every
  // channel switch causes) an already-unfurled URL renders its card in the
  // same paint — the post-paint pop-in was a per-card layout shift that broke
  // the list's bottom anchoring. `settled: false` means genuinely unknown, and
  // renders a fixed-height placeholder instead of nothing for the same reason.
  const [state, setState] = useState(() => {
    if (!cardPath) return { preview: null, settled: true };
    const cached = getCachedLinkPreview(url);
    return { preview: cached ?? null, settled: cached !== undefined };
  });
  const [imgFailed, setImgFailed] = useState(false);
  const preview = state.preview;

  useEffect(() => {
    setImgFailed(false);
  }, [url]);

  useEffect(() => {
    if (!cardPath) {
      setState({ preview: null, settled: true });
      return undefined;
    }
    const cached = getCachedLinkPreview(url);
    if (cached !== undefined) {
      setState({ preview: cached, settled: true });
      return undefined;
    }
    setState({ preview: null, settled: false });
    let alive = true;
    fetchLinkPreview(url).then((p) => {
      if (alive) setState({ preview: p, settled: true });
    });
    return () => {
      alive = false;
    };
  }, [cardPath, url]);

  if (!enabled || !url) return null;

  if (embedSrc) {
    return (
      <div className="chat-link-video-wrap">
        <iframe
          src={embedSrc}
          title="Video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    );
  }

  if (directImage) {
    if (imgFailed) return null;
    return (
      <div className="chat-link-image-wrap">
        <img
          className="chat-link-image"
          src={url}
          alt=""
          loading="lazy"
          onClick={() => openExternalUrl(url)}
          onError={() => setImgFailed(true)}
        />
      </div>
    );
  }

  // Unknown URL, fetch in flight — hold the card's slot so the answer doesn't
  // reflow the conversation. Resolving to "no preview" collapses it once,
  // and that verdict is cached so it never re-shows for this URL.
  if (!preview && !state.settled) {
    return <div className="chat-link-preview chat-link-preview--pending" aria-hidden />;
  }

  if (!preview) return null;

  return (
    <div
      className="chat-link-preview"
      role="link"
      tabIndex={0}
      onClick={() => openExternalUrl(preview.url)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openExternalUrl(preview.url);
        }
      }}
    >
      {/* Text first, then a full-width image beneath it — the shape Discord and
          Slack use. The old side-by-side card put the image in an 88px square
          with `object-fit: cover`, so a wide OG banner (which is what almost
          every site publishes, at 1200x630) was cropped to its middle and the
          product name in it was never visible. */}
      <div className="chat-link-preview-body">
        {preview.siteName && <div className="chat-link-preview-site">{preview.siteName}</div>}
        {preview.title && <div className="chat-link-preview-title">{preview.title}</div>}
        {preview.description && (
          <div className="chat-link-preview-desc">{preview.description}</div>
        )}
      </div>
      {preview.image && (
        <div className="chat-link-preview-img">
          <img src={preview.image} alt="" loading="lazy" />
        </div>
      )}
    </div>
  );
}
